import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '91在线视频',
      url: 'https://www.91onlinevideo.com',
      description: '91在线视频 - 精选91精品视频在线、91精品视频在线播放等优质内容',
    },
    {
      '@type': 'WebSite',
      name: '91在线视频',
      url: 'https://www.91onlinevideo.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.91onlinevideo.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      name: '91在线视频 | 91精品视频在线与91精品视频在线播放 | 精选高分热门推荐在线观看',
      url: 'https://www.91onlinevideo.com/',
      description: '91在线视频精选91精品视频在线、91精品视频在线播放等优质内容，涵盖精品影视、高分电影、热门推荐及精选视频大全',
    },
  ],
};

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredVideos, setFilteredVideos] = useState(videos);
  const [heroIndex, setHeroIndex] = useState(0);

  const heroVideos = videos.filter((v) => v.rating >= 9.2).slice(0, 4);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroVideos.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroVideos.length]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredVideos(videos);
    } else {
      setFilteredVideos(videos.filter((v) => v.category === activeCategory));
    }
  }, [activeCategory]);

  const hotVideos = videos.filter((v) => v.rating >= 9.0).slice(0, 8);
  const latestArticles = articles.slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }} />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[580px] overflow-hidden">
          {heroVideos.map((video, index) => (
            <div
              key={video.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>

          <div className="absolute inset-0 flex flex-col items-center justify-center w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white text-center mb-3">
              91在线视频
            </h1>
            <p className="text-sm md:text-base text-white/80 text-center max-w-2xl mb-6">
              精选91精品视频在线、91精品视频在线播放等优质内容<br className="hidden sm:block" />
              涵盖精品影视、高分电影、热门推荐及精选视频大全，高清在线观看不缓冲
            </p>
            <div className="flex items-center gap-2 w-full max-w-lg">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="搜索你感兴趣的内容..."
                  className="w-full px-4 py-3 pl-10 bg-white/15 backdrop-blur-md border border-white/20 rounded-full text-white placeholder-white/50 text-sm focus:outline-none focus:border-primary-500 transition-colors"
                />
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50 text-sm"></i>
              </div>
              <Link
                to="/category/hot"
                className="px-5 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                热门推荐
              </Link>
            </div>

            <div className="flex items-center gap-2 mt-8">
              {heroVideos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    index === heroIndex ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`切换到第${index + 1}张`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 lg:px-8 -mt-8 relative z-10">
          <div className="bg-background-100 rounded-xl p-1 border border-background-200/30 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-400 hover:text-foreground-800 hover:bg-background-200/50'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="w-full px-4 md:px-6 lg:px-8 pt-10 md:pt-14">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                🔥 热门推荐
              </h2>
              <p className="text-xs md:text-sm text-foreground-400 mt-1">
                精选高分热门影视，91精品视频在线播放
              </p>
            </div>
            <Link
              to="/category/hot"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {hotVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Category Videos */}
        <section className="w-full px-4 md:px-6 lg:px-8 pt-10 md:pt-14">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                {activeCategory === 'all' ? '📺 精选视频' : `📺 ${categories.find((c) => c.slug === activeCategory)?.name || '精选视频'}`}
              </h2>
              <p className="text-xs md:text-sm text-foreground-400 mt-1">
                91精彩视频在线观看，高清画质流畅体验
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {filteredVideos.slice(0, 8).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Featured Articles */}
        {latestArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-8 pt-10 md:pt-14 pb-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
                  📝 精彩资讯
                </h2>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">
                  影视资讯、深度影评、幕后故事
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {latestArticles.slice(0, 1).map((article) => (
                <div key={article.id} className="md:col-span-2">
                  <ArticleCard article={article} variant="featured" />
                </div>
              ))}
              {latestArticles.slice(1).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>
        )}

        {/* Stats Section */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
          <div className="bg-background-100 rounded-xl p-6 md:p-10 border border-background-200/30">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">10万+</div>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">精品影视资源</p>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">5000万+</div>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">累计播放次数</p>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">9.2</div>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">平均用户评分</p>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500">99.9%</div>
                <p className="text-xs md:text-sm text-foreground-400 mt-1">播放流畅率</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}